// A battery set's page (uptick_plan.md §12, 2026-11-03): its battery ID (B-XXXX-XXXX, created by the tester on the
// set's first test and printed on its label), its tests, and — for Uptick orgs — its link to an Uptick battery set,
// with the one-time "Link to an Uptick battery set" (property → asset). Reached from a test page or by scanning the
// label's QR with a phone on site, so it stays a plain single column.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { useSession } from "../lib/auth.ts";
import {
  batteryApi, uptickApi, uptickPushApi, testResultLabel, testResultPillClass, testTypeLabel,
  type UptickPropertyHit,
} from "../lib/api.ts";

export function BatteryPage() {
  const { batteryId } = useParams<{ batteryId: string }>();
  const { data: session } = useSession();
  const orgId = session?.session.activeOrganizationId;
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [prop, setProp] = useState<UptickPropertyHit | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [linking, setLinking] = useState(false);

  const info = useQuery({
    queryKey: ["battery", orgId, batteryId],
    queryFn: () => batteryApi.get(orgId!, batteryId!),
    enabled: !!orgId && !!batteryId,
    retry: false,
  });
  const uptick = useQuery({
    queryKey: ["org", orgId, "uptick"],
    queryFn: () => uptickApi.status(orgId!),
    enabled: !!orgId,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const connected = uptick.data?.connected === true;
  const picking = connected && linking;
  const hits = useQuery({
    queryKey: ["org", orgId, "uptick-props", q],
    queryFn: () => uptickPushApi.searchProperties(orgId!, q),
    enabled: picking && !prop && q.trim().length > 1,
    retry: false,
  });
  const assets = useQuery({
    queryKey: ["org", orgId, "uptick-passets", prop?.propertyId, showAll],
    queryFn: () => uptickPushApi.propertyAssets(orgId!, prop!.propertyId, showAll),
    enabled: picking && !!prop,
    retry: false,
  });
  const link = useMutation({
    mutationFn: (assetId: number | null) => batteryApi.link(orgId!, batteryId!, assetId),
    onSuccess: () => {
      setLinking(false); setProp(null); setQ("");
      qc.invalidateQueries({ queryKey: ["battery", orgId, batteryId] });
    },
  });

  if (info.isLoading) return <p className="muted">Loading…</p>;
  if (info.isError) {
    const msg = (info.error as Error).message;
    return (
      <section>
        <h2>Battery {batteryId}</h2>
        <p className={msg.includes("not-found") ? "muted" : "err"}>
          {msg.includes("not-found") ? "No test with this battery ID has reached BT-1851 Cloud yet."
            : msg.includes("bad-battery-id") ? "This is not a valid battery ID — check the label." : msg}
        </p>
      </section>
    );
  }
  const b = info.data!;

  return (
    <section style={{ maxWidth: "44em" }}>
      <h2>Battery <code>{b.battery_id}</code></h2>
      {b.collision_test_id && (
        <p className="err">
          A later test (<Link to={`/tests/${b.collision_test_id}`}>open it</Link>) created this ID again — two battery
          sets may carry the same label. Check the batteries on site.
        </p>
      )}

      {connected && (
        <div className="card">
          <h4 style={{ marginTop: 0 }}>Uptick</h4>
          {b.uptick_asset_id ? (
            <p>
              Linked to Uptick battery set <strong>A-{b.uptick_asset_id}</strong>
              <span className="muted">
                {b.linked_by === "push" ? " (learnt when a result was sent)" : ""}
                {b.linked_at ? ` · ${new Date(b.linked_at).toLocaleDateString()}` : ""}
              </span>{" "}
              <button type="button" disabled={link.isPending} onClick={() => setLinking(true)}>Change</button>{" "}
              <button type="button" disabled={link.isPending} onClick={() => link.mutate(null)}>Unlink</button>
            </p>
          ) : (
            <p>
              Not linked to an Uptick battery set yet. Link it once — results with this ID then go to that set&rsquo;s
              work orders.{" "}
              {!linking && <button type="button" onClick={() => setLinking(true)}>Link to an Uptick battery set</button>}
            </p>
          )}
          {picking && (
            <>
              {!prop ? (
                <>
                  <input placeholder="Search the property…" value={q} onChange={(e) => setQ(e.target.value)}
                    style={{ width: "100%", marginBottom: "0.5em" }} />
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {(hits.data?.properties ?? []).map((h) => (
                      <li key={h.propertyId}>
                        <button type="button" style={{ width: "100%", textAlign: "left", margin: "0.15em 0" }}
                          onClick={() => setProp(h)}>{h.label ?? `property ${h.propertyId}`}</button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <p>Property: <strong>{prop.label ?? prop.propertyId}</strong>{" "}
                    <button type="button" onClick={() => setProp(null)}>change</button></p>
                  {assets.isPending ? <p className="muted">Loading battery sets…</p> : (
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {(assets.data?.assets ?? []).map((a) => (
                        <li key={a.assetId}>
                          <button type="button" disabled={link.isPending}
                            style={{ width: "100%", textAlign: "left", margin: "0.15em 0" }}
                            onClick={() => link.mutate(a.assetId)}>
                            {a.ref ? `${a.ref} — ` : ""}{a.label ?? `asset ${a.assetId}`}{a.typeName ? ` · ${a.typeName}` : ""}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {assets.data?.filtered !== "none" && !showAll && (
                    <p className="muted" style={{ fontSize: "0.85em" }}>
                      Battery asset types only. <button type="button" onClick={() => setShowAll(true)}>Show all asset types</button>
                    </p>
                  )}
                </>
              )}
              <p><button type="button" onClick={() => { setLinking(false); setProp(null); }}>Cancel</button></p>
            </>
          )}
          {link.isError && <p className="err">{(link.error as Error).message}</p>}
        </div>
      )}

      <h3>Tests</h3>
      <div className="card">
        {b.tests.length === 0 ? <p className="muted">No tests yet.</p> : (
          <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
            {b.tests.map((t) => (
              <li key={t.id}>
                <Link to={`/tests/${t.id}`}>{new Date(t.started_at).toLocaleString()}</Link>{" "}
                <span className="muted">{testTypeLabel(t.test_type)}</span>{" "}
                <span className={testResultPillClass(t.test_result)}>{testResultLabel(t.test_result)}</span>
                {t.name && t.name !== b.battery_id ? <span className="muted"> · {t.name}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
